import clsx from 'clsx'
import { get, isNil, orderBy } from 'lodash-es'
import { type MouseEvent, type ReactNode, useEffect, useState } from 'react'
import type { FieldPath } from 'react-hook-form'

import { Icon, LoadMore } from '@fv/client-components'
import {
  flattenPages,
  type InfiniteData,
  type Page,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@fv/client-core'
import type { HintedString } from '@fv/models/core'

import type { SortDirection } from '@/features/dispatches/types'

import { AdminButton } from './AdminButton'
import { AdminCheckbox } from './AdminCheckbox'
import { AdminLoading } from './AdminLoading'

export type SortFn<T> = (data: T[], dir: SortDirection) => T[]
export type ColumnDef<T extends object> = {
  hidden?: boolean
  checkbox?: {
    enabled: boolean
    checked?: boolean
    disabled?: boolean
    onToggle?: (checked: boolean) => void
  }
  key: HintedString<FieldPath<T>>
  label?: ReactNode
  render?: (data: T) => ReactNode
  exportValue?: (data: T) => string | number | boolean | null | undefined
  sort?: SortFn<T>
  sortable?: boolean
  defaultValue?: any
  className?: string
  pin?: boolean
}

export type SortDef<T extends object = object> = {
  key: HintedString<FieldPath<T>>
  dir: SortDirection
}
export type AdminTableProps<T extends object> = {
  columns: Array<ColumnDef<T>>
  data?: T[]
  rowClassName?: (data: T) => string | undefined | null | object
  initialState?: {
    sort: SortDef<T>
  }
  rowKey: (data: T) => string
  onRowSelect?: (data: T) => void
  onSortChange?: (sort: SortDef<T>) => void
  disableSort?: boolean
  emptyContent?: ReactNode
  defaultSort?: string
  defaultSortDir?: SortDirection
  className?: string
  defaultValue?: any
  collapsible?: boolean
  stickyHeader?: boolean
}

type ColHeaderProps<T extends object> = {
  col: ColumnDef<T>
  sort: SortDef<T>
  onSort: (sort: SortDef<T>) => void
  disableSort?: boolean
}
const ColumnHeader = <T extends object>({
  col,
  sort,
  onSort,
  disableSort,
}: ColHeaderProps<T>) => {
  return (
    <div className="flex items-center gap-2">
      {col.checkbox?.enabled && (
        <AdminCheckbox
          id={`${col.key}_all`}
          checked={col.checkbox.checked}
          disabled={col.checkbox.disabled}
          onChange={e => col.checkbox?.onToggle?.(e.target.checked)}
        />
      )}
      <button
        disabled={disableSort || col.sortable === false}
        className={clsx({
          'cursor-default': disableSort || col.sortable === false,
        })}
        onClick={() => {
          if (disableSort || col.sortable === false) return
          onSort({
            key: col.key,
            dir: sort.key === col.key && sort.dir === 'asc' ? 'desc' : 'asc',
          })
        }}
      >
        {col.label ?? col.key.toString()}
        {!disableSort && col.label && sort.key === col.key && (
          <Icon
            className="ml-3"
            icon={sort.dir === 'desc' ? 'chevron-down' : 'chevron-up'}
          />
        )}
      </button>
    </div>
  )
}

export const AdminTable = <T extends object>({
  className,
  columns,
  data,
  disableSort,
  emptyContent,
  initialState,
  onRowSelect,
  onSortChange,
  rowClassName,
  rowKey,
  defaultSort,
  defaultSortDir,
  collapsible,
  stickyHeader,
}: AdminTableProps<T>) => {
  const sortableCols = columns.filter(c => c.sortable !== false)
  const [sort, setSort] = useState<SortDef<T>>(
    initialState?.sort ?? {
      key: defaultSort ?? sortableCols[0]?.key,
      dir: defaultSortDir ?? 'asc',
    },
  )
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    if (onSortChange) {
      onSortChange(sort)
    }
  }, [sort, onSortChange])
  if (!data?.length && emptyContent) {
    return <>{emptyContent}</>
  }

  let sortedData = data ?? []
  if (!onSortChange && !disableSort) {
    const colDef = columns.find(c => c.key === sort.key && c.sortable !== false)
    if (colDef?.sort) {
      sortedData = colDef.sort(data ?? [], sort.dir)
    } else if (
      colDef &&
      (!isNil(get(data?.[0], sort.key)) || !isNil(colDef.defaultValue))
    ) {
      sortedData = orderBy(
        data,
        d => {
          const propVal = get(d, sort.key)
          return typeof propVal === 'string'
            ? propVal.toLowerCase()
            : (propVal ?? colDef.defaultValue)
        },
        [sort.dir],
      )
    }
  }

  const handleRowClick = (d: T) => (e: MouseEvent<HTMLTableRowElement>) => {
    if ((e.target as HTMLElement)?.closest('button,a')) return

    onRowSelect?.(d)
    e.stopPropagation()
  }

  return (
    <table className={clsx('table-zebra-zebra table', className)}>
      <thead>
        <tr className={clsx('sticky top-0 z-10 bg-base-200', stickyHeader)}>
          {columns.map(c => {
            const CellTag = c.pin ? `th` : 'td'

            return (
              <CellTag
                key={`th.${c.key}`}
                className={clsx({ hidden: c.hidden }, c.className)}
              >
                <ColumnHeader
                  col={c}
                  sort={sort}
                  onSort={setSort}
                  disableSort={disableSort}
                />
              </CellTag>
            )
          })}
          {collapsible && (
            <AdminButton
              className="btn-sm btn-ghost"
              onClick={e => {
                setExpanded(p => !p)
                e.stopPropagation()
              }}
              icon={expanded ? 'chevron-down' : 'chevron-up'}
              transform="grow-4"
            />
          )}
        </tr>
      </thead>
      <tbody>
        {expanded &&
          sortedData.map(d => {
            const key = rowKey(d)
            return (
              <tr
                id={key}
                className={clsx(rowClassName?.(d), {
                  'transition-colors cursor-pointer hover:!bg-primary/10':
                    !!onRowSelect,
                })}
                onClick={handleRowClick(d)}
                key={rowKey(d)}
              >
                {columns.map(col => {
                  const CellTag = col.pin ? `th` : 'td'
                  return (
                    <CellTag
                      key={`${key}.${col.key}`}
                      className={clsx({ hidden: col.hidden }, col.className)}
                      {...(col.exportValue
                        ? { 'data-export-value': col.exportValue(d) }
                        : {})}
                    >
                      {col.render?.(d) ?? (get(d, col.key) || col.defaultValue)}
                    </CellTag>
                  )
                })}
              </tr>
            )
          })}
      </tbody>
    </table>
  )
}

type AdminQueryTableProps<T extends object> = Omit<
  AdminTableProps<T>,
  'data'
> & {
  query: UseQueryResult<T[], unknown>
}

export const AdminQueryTable = <T extends object>({
  query,
  ...props
}: AdminQueryTableProps<T>) => {
  const data = query.data ?? []
  const emptyContent =
    query.isFetched && !query.data?.length ? props.emptyContent : undefined
  return (
    <div className="relative">
      {(query.isFetched || query.isFetching) && (
        <AdminTable data={data} emptyContent={emptyContent} {...props} />
      )}
      {query.isFetching && (
        <div className="bg-neutral/90 text-neutral-content absolute left-0 top-0 flex h-full min-h-[200px] w-full justify-center pt-10 text-lg ">
          <div>
            <AdminLoading />
          </div>
        </div>
      )}
    </div>
  )
}

type AdminInfiniteQueryTableProps<TModel extends object> = Omit<
  AdminQueryTableProps<TModel>,
  'query'
> & {
  query: UseInfiniteQueryResult<InfiniteData<Page<TModel>>>
}

export const AdminInfiniteQueryTable = <TModel extends object>({
  query,
  ...props
}: AdminInfiniteQueryTableProps<TModel>) => {
  const data = flattenPages(query.data?.pages)
  const emptyContent =
    query.isFetched && !data?.length ? props.emptyContent : undefined
  return (
    <div className="relative">
      {(query.isFetched || query.isFetching) && (
        <>
          <AdminTable data={data} emptyContent={emptyContent} {...props} />
          <LoadMore
            fetchNextPage={query.fetchNextPage}
            hasNextPage={query.hasNextPage}
            isLoading={query.isFetchingNextPage}
          />
        </>
      )}
      {query.isLoading && (
        <div className="bg-neutral/90 text-neutral-content absolute left-0 top-0 flex h-full min-h-[200px] w-full justify-center pt-10 text-lg ">
          <div>
            <AdminLoading />
          </div>
        </div>
      )}
    </div>
  )
}
